import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { ChatStartersPaginationParams } from '@mm/state/controllers/ChatStartersController';
import { mapUIFilterKindToChatStarterKind } from '@mm/state/helpers/chat-starters-helpers';
import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import {
  selectChatStarterRecordsByUiFilterKind,
  selectChatStartersInvalidRecords,
} from '@mm/state/selectors/chatStarters';
import { selectInstagramAccountId } from '@mm/state/selectors/instagramSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { debounce } from 'lodash';
import * as queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { isStarterKind } from './types';

const DEFAULT_PER_PAGE_QTY = 8;
const DEFAULT_PAGE = 1;

const parseKind = (search: string): UIFilterStarterKindType => {
  const { kind } = queryString.parse(search);
  return isStarterKind(kind) ? kind : 'facebook_page_welcomer';
};

const parseSearch = (search: string): string | undefined => {
  const { search: parsedSearch } = queryString.parse(search);
  return typeof parsedSearch === 'string' ? parsedSearch : undefined;
};

const useChatStartersFilters = () => {
  const { history, location } = useRouter();
  const { search } = location;
  const activeKind = parseKind(search);
  const activeSearch = parseSearch(search);
  const [doSearch, setDoSearch] = useState(false);

  const setSearchParams = (params: Record<string, any>) => {
    const searchParams = queryString.parse(search);
    history.push({
      ...location,
      search: queryString.stringify({ ...searchParams, ...params }),
    });
  };

  const setFilterKind = (kind?: UIFilterStarterKindType) =>
    setSearchParams({ kind, search: undefined });

  const setSearchQuery = (search: string) => {
    setDoSearch(true);
    setSearchParams({ search: search || undefined });
  };

  return {
    activeKind,
    activeSearch,
    setFilterKind,
    setSearchQuery,
    doSearch,
    setDoSearch,
  };
};

// Same algorithm is used in campaigns. Potentially can be used as a base for any collection view.
// Or abstracted further with strategy params for retrieval / query.
export const useChatStarters = () => {
  const {
    activeKind,
    setFilterKind,
    activeSearch,
    setSearchQuery,
    doSearch,
    setDoSearch,
  } = useChatStartersFilters();

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const invalidRecords = useSelector(selectChatStartersInvalidRecords);
  const starterRecords = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, activeKind),
  );
  const instagramAccountId = useSelector(selectInstagramAccountId);

  const { retrieveAll, invalidateAll } = useChatStartersCRUDActions();

  const fetchCallback = useCallback(
    ({
      uiFilterKind,
      pagination,
      search,
    }: {
      uiFilterKind: UIFilterStarterKindType;
      pagination?: ChatStartersPaginationParams;
      search?: string;
    }) => {
      const kind = mapUIFilterKindToChatStarterKind(uiFilterKind);
      if (kind) {
        retrieveAll({
          botId: activeChatbotId,
          kind,
          search,
          pagination,
        });
      }
    },
    [activeChatbotId, retrieveAll],
  );

  const fetchCurrentActiveKind = useCallback(
    (pagination?: ChatStartersPaginationParams) => {
      fetchCallback({
        uiFilterKind: activeKind,
        search: activeSearch,
        pagination,
      });
    },
    [activeKind, activeSearch, fetchCallback],
  );

  const ref = useRef(debounce((fn: Function) => fn(), 1000));
  const fetchCurrentActiveKindThrottled = () =>
    ref.current(fetchCurrentActiveKind);

  const changePage = (pageNumber: number) => {
    if (pageNumber && starterRecords?.pagination) {
      fetchCurrentActiveKind({
        page: pageNumber,
        perPage: starterRecords.pagination.perPage,
      });
    }
  };
  useEffect(() => {
    fetchCurrentActiveKind({
      page: starterRecords?.pagination?.page || DEFAULT_PAGE,
      perPage: starterRecords?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
    });
  }, [activeKind, instagramAccountId]);

  useEffect(() => {
    if (invalidRecords[activeKind]) {
      fetchCurrentActiveKind({
        page: starterRecords?.pagination?.page || DEFAULT_PAGE,
        perPage: starterRecords?.pagination?.perPage || DEFAULT_PER_PAGE_QTY,
      });
    }
  }, [invalidRecords[activeKind]]);

  useEffect(() => {
    if (doSearch) {
      fetchCurrentActiveKindThrottled();
      setDoSearch(false);
    }
  }, [activeSearch]);

  useEffect(invalidateAll, [activeChatbotId]);

  // needed to retrieve / update counters for all groups
  const isKindInvalidated = !!invalidRecords[activeKind];
  useEffect(() => fetchCallback({ uiFilterKind: 'all' }), [isKindInvalidated]);

  return {
    starterRecords,
    setFilterKind,
    activeKind,
    activeSearch,
    setSearchQuery,
    changePage,
  };
};
